import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { uploadPhotos, updateProfile } from '../api';
import './CompleteProfile.css';

const CompleteProfile: React.FC = () => {
  const { user, refetchUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || '');
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [photos, setPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length !== 3) {
      setError('Please select exactly 3 photos');
      return;
    }

    setPhotos(files);
    setError('');

    // Create previews
    const newPreviews: string[] = [];
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result as string);
        if (newPreviews.length === files.length) {
          setPreviews(newPreviews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (photos.length !== 3) {
      setError('Please select exactly 3 photos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Update name and gender
      await updateProfile({ name, gender });

      // Upload photos
      const formData = new FormData();
      photos.forEach((photo) => {
        formData.append('photos', photo);
      });

      await uploadPhotos(formData);
      await refetchUser();
      navigate('/rating');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="complete-profile-container">
      <div className="complete-profile-content">
        <h1>Complete Your Profile</h1>
        <p className="subtitle">Upload 3 photos and set your details</p>

        <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              required
            />
          </div>

          <div className="form-group">
            <label>Gender</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value as 'male' | 'female')}
              required
              style={{
                background: '#1a1a1a',
                border: '2px solid #3d3d3d',
                borderRadius: '10px',
                padding: '12px 16px',
                color: '#ffffff',
                fontSize: '16px',
                width: '100%',
              }}
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          <div className="form-group">
            <label>Upload Photos (exactly 3)</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoChange}
              required
            />
          </div>

          {previews.length > 0 && (
            <div className="photo-previews">
              {previews.map((preview, index) => (
                <div key={index} className="photo-preview">
                  <img src={preview} alt={`Preview ${index + 1}`} />
                </div>
              ))}
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <button type="submit" disabled={loading || photos.length !== 3}>
            {loading ? 'Uploading...' : 'Complete Profile'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CompleteProfile;
